import cloudflareAssets from "@/assets/cloudflareAssets";
import type { ILoanSchedule } from "@/modules/loan/common/types";
import { useGetLoanScheduleQuery } from "@/modules/loan/services";
import { INITIAL_PAGE, PageSizeOptions } from "@/shared/common/constants";
import { CloudflareAsset, ErrorMessages, TableNames } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, Select } from "@/shared/components";
import { getErrorMessage, getTableName } from "@/shared/helpers";
import { useTableHooks } from "@/shared/hooks";
import {
  AdvancedTable as Table,
  ColumnSettings
} from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getColumns } from "./columns";

export const LoanSchedule = () => {
  const { loanId = "" } = useParams();
  const { t } = useTranslation();
  const [loanSchedule, setLoanSchedule] = useState<ILoanSchedule[]>([]);

  const {
    page,
    pageSize,
    tableRef,
    hasNextPage,
    setHasNextPage,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    updateHasNextPage,
  } = useTableHooks<ILoanSchedule>();

  const {
    data: list,
    isFetching,
    isError,
    error,
  } = useGetLoanScheduleQuery(
    {
      id: loanId,
      pageIndex: page,
      pageSize,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const columns = getColumns({ t });

  useEffect(() => {
    if (!list) return;

    if (list?.length || (!list?.length && page === INITIAL_PAGE)) {
      setLoanSchedule(list);
      updateHasNextPage(list.length);
    } else if (!list?.length && page !== INITIAL_PAGE) {
      onPrevPage();
      setHasNextPage(false);
    }
  }, [list]);

  const tableName = getTableName(TableNames.LoanSchedule);

  return (
    <div className="position-relative page-body">
      <div className="full-width flexbox justify-content--center">
        <div
          className={classNames("advanced_table-wrapper loan-schedule-table", {
            "table-full": loanSchedule.length,
            "table-empty": !loanSchedule.length,
          })}>
          <div className="full-width pb-24">
            <Heading className="content-title" weight="bolder" size="xsmall">
              {t("loans.loanSchedule")}
            </Heading>
          </div>
          <Table
            data={loanSchedule || []}
            withSelect
            columns={columns}
            isLoading={isFetching || !tableName}
            hasError={isError}
            tableSettings={{
              tableName: tableName || "",
              persistColumnSettings: "localStorage"
            }}
            emptyTitle={
              isError ?
                t(getErrorMessage(error as IError, ErrorMessages.TryAgainShortly)) :
                t("general.noData")
            }
            emptySubTitle={
              !isError ? t("general.noDataAvailable") : ""
            }
            emptyIllustration={cloudflareAssets[CloudflareAsset.EmptyContent]}
            renderHeader={(table) => (
              <div className="advanced-table__header flexbox justify-content--end py-16 px-24">
                <div className="flexbox">
                  <ColumnSettings
                    table={table}
                    allToggleText={t("general.all")}
                    tooltipText={t("general.columnCantBeHidden")}
                  />
                </div>
              </div>
            )}
            renderFooter={(table) => {
              tableRef.current = table;
              return (
                <div className="advanced-table__footer">
                  <div className="advanced-table__pagination">
                    <Select
                      setSelectedValue={(value) => onPageSizeChange(value as string)}
                      selectedValue={`${pageSize}`}
                      options={PageSizeOptions}
                    />
                    <div className="flexbox align-items--center">
                      <Button
                        onClick={onPrevPage}
                        size="medium"
                        type="tertiary"
                        data-testid="loan-schedule-prev-page"
                        data-ga-id="loan-schedule-prev-page"
                        data-ga-action="Loan schedule table previos page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                          ),
                        }}
                        disabled={page === INITIAL_PAGE}
                      />
                      <Button
                        onClick={onNextPage}
                        size="medium"
                        type="tertiary"
                        data-testid="loan-schedule-next-page"
                        data-ga-id="loan-schedule-next-page"
                        data-ga-action="Loan schedule table next page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                          ),
                        }}
                        disabled={!hasNextPage}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
